"use client";

import z from "zod";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import { useForm } from "@tanstack/react-form";

import styles from "./LoginForm.module.scss";

import AtSymbolIcon from "@/icons/at-symbol.svg";
import LockSolidIcon from "@/icons/lock-solid.svg";

import {
  Button,
  ButtonAppearance,
  ButtonSize,
  ButtonVariant,
} from "@/dls/Button";
import { Divider } from "../Divider";
import { PasswordFieldNew } from "../PasswordField";
import { ErrorMessage } from "../ErrorMessage";
import { AppField } from "@/dls/AppField";

import {
  useCredentialsSignIn,
  usePasskeySignIn,
  useSocialSignIn,
} from "@/tanstackQuery";
import { useVisibleAppSettings } from "@/tanstackQuery/queries/appSettings";
import { APP_SETTING_KEY } from "@/api/appSettings";
import { usePreloadPasskeys } from "@/hooks/usePreloadPasskeys";
import { getFieldErrorState } from "@/utils/tanstackForm";
import { withPreventDefault } from "@/utils/actions";
import { useErrorMap } from "@/hooks/useErrorMap";
import { validate } from "@/utils/zod";

interface Props {}

interface Values {
  username: string;
  password: string;
}

const USERNAME_MIN_LENGTH = 3;
const PASSWORD_MIN_LENGTH = 4;

const createValidationSchema = (t: any) => {
  return {
    username: z
      .string()
      .min(
        USERNAME_MIN_LENGTH,
        t("validation.min_length", { value: USERNAME_MIN_LENGTH })
      )
      .regex(/^[a-zA-Z0-9_]+$/, t("validation.invalid_username")),
  };
};

const createOnChangeValidation = (t: any) => {
  return {
    username: z
      .string()
      .regex(/^[a-zA-Z0-9_]+$/, t("validation.invalid_username")),
  };
};

const getValuesValidation = (values: Values) => {
  return (
    values.username.length >= USERNAME_MIN_LENGTH &&
    values.password.length >= PASSWORD_MIN_LENGTH
  );
};

const LoginForm: React.FC<Props> = () => {
  const signIn = useCredentialsSignIn();
  const signInWithPasskey = usePasskeySignIn();
  const signInWithSocial = useSocialSignIn();

  const signInErrors = useErrorMap(signIn.error);

  const t = useTranslations();

  const validation = useMemo(() => {
    const blur = createValidationSchema(t);
    const change = createOnChangeValidation(t);
    return { blur, change };
  }, [t]);

  const onBlurValidation = useCallback(
    ({ value, fieldApi }) => {
      if (value.toString().length > 0) {
        const schema = validation.blur[fieldApi.name];
        if (schema) return validate(schema, value);
      }
    },
    [validation.blur]
  );

  const onChangeValidation = useCallback(
    ({ value, fieldApi }) => {
      if (value.toString().length > 0) {
        const schema = validation.change[fieldApi.name];
        if (schema) return validate(schema, value);
      }
    },
    [validation.change]
  );

  const form = useForm({
    defaultValues: {
      username: "",
      password: "",
    },
    onSubmit({ value }) {
      signIn.mutate(value);
    },
  });

  const errors = useErrorMap(signIn.error);

  const { data: visibleSettings } = useVisibleAppSettings();
  const maintenanceSetting = visibleSettings?.[APP_SETTING_KEY.Maintenance];
  const maintenanceMessage =
    maintenanceSetting?.enabled
      ? (maintenanceSetting.message?.trim() ?? "")
      : "";

  const isMaintenanceError =
    errors.tranKey === "errors.maintenance_mode_active" &&
    !!errors.message?.trim();

  // Preload passkeys
  usePreloadPasskeys();

  return (
    <form
      onSubmit={withPreventDefault(form.handleSubmit)}
      className={styles.form}
    >
      <form.Field
        name="username"
        validators={{
          onBlur: onBlurValidation,
          onChange: onChangeValidation,
        }}
        children={(field) => (
          <AppField
            prefix={<AtSymbolIcon />}
            autoCapitalize="off"
            autoComplete="username webauthn"
            label={t("fields.username")}
            value={field.state.value}
            onValueChange={field.handleChange}
            onBlur={field.handleBlur}
            {...getFieldErrorState(field)}
          />
        )}
      />
      <form.Field
        name="password"
        validators={{
          onBlur: onBlurValidation,
          onChange: onChangeValidation,
        }}
        children={(field) => (
          <PasswordFieldNew
            prefix={<LockSolidIcon />}
            // autoComplete="new-password"
            label={t("fields.password")}
            value={field.state.value}
            onValueChange={field.handleChange}
            onBlur={field.handleBlur}
            {...getFieldErrorState(field)}
          />
        )}
      />

      {isMaintenanceError ? (
        <ErrorMessage
          message={errors.message}
          className={styles.errorMessage}
        />
      ) : maintenanceMessage ? (
        <ErrorMessage
          message={maintenanceMessage}
          className={styles.errorMessage}
        />
      ) : (
        <ErrorMessage
          message={errors.message}
          tranKey={errors.tranKey}
          tranParams={errors.tranParams}
          className={styles.errorMessage}
        />
      )}

      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          getValuesValidation(state.values),
        ]}
        children={([canSubmit, isValid]) => (
          <Button
            type="submit"
            disabled={!canSubmit || !isValid}
            isLoading={signIn.isPending}
            className={styles.submitButton}
            variant={ButtonVariant.Primary}
            size={ButtonSize.Large}
          >
            {t("common.login")}
          </Button>
        )}
      />

      <Divider label={t("common.or")} />

      <Button
        type="button"
        size={ButtonSize.Large}
        className={styles.passkeyButton}
        variant={ButtonVariant.Secondary}
        appearance={ButtonAppearance.Outlined}
        isLoading={signInWithPasskey.isPending}
        onClick={() => signInWithPasskey.mutate({})}
      >
        {t("common.continue_with_passkey")}
      </Button>
    </form>
  );
};

export { LoginForm };
